import React, { useState, useEffect, ChangeEvent, ChangeEventHandler } from "react";
import { useParams } from "react-router-dom";
import { useKeycloak } from '@react-keycloak/web';
import OrganizationModal from "./OrganizationModal";
import { toast } from 'react-toastify';
import { Organization, buildOrganization } from "../common/ObjectTypes";
import { addOrganization, updateOrganization } from "../../api/organizations/organizationsApi";
import { useTranslation } from "react-i18next";

type ManageOrganizationModalProps = {
    onToggleModal: (saved?: boolean) => void
    existingOrg?: Organization,
    newOrg: boolean
}

export function ManageOrganizationModal({
    onToggleModal,
    existingOrg,
    newOrg
}: ManageOrganizationModalProps) {
    const { keycloak } = useKeycloak();
    const { t } = useTranslation();
    const { org_id } = useParams();
    const toggleModalMouseEvent: React.MouseEventHandler<HTMLButtonElement> = () => onToggleModal();
    const [organizaton, setOrganization] = useState<Organization>(existingOrg ? existingOrg : () => buildOrganization())
    const [errors, setErrors] = useState({});

    function handleChange(event: ChangeEvent<HTMLInputElement>) {
        const item = event.target
        const item_id: string = item.id
        setOrganization((prevOrg: Organization) => ({
            ...prevOrg,
            [item_id]: item.value,
        }))
    }

    // Update modal starts with the existing workspace, so the Save button stays
    // disabled until the user actually edits a field. Add modal only needs a name.
    const isDirty = !!existingOrg && (
        (organizaton.name ?? "") !== (existingOrg.name ?? "")
        || (organizaton.description ?? "") !== (existingOrg.description ?? "")
    );
    const saveDisabled = !organizaton.name?.trim() || (!newOrg && !isDirty);

    const formIsValid = () => {
        let form_errors: any = {};
        if (organizaton.name.length === 0) form_errors.name = "Name is required.";
        if (organizaton.name.length > 30) form_errors.name = "Name cannot exceed 30 characters.";
        setErrors(form_errors);
        return Object.keys(form_errors).length === 0
    }

    async function handleAddOrganization() {
        if (!formIsValid()) return;
        else {
            if (keycloak.token) {
                await addOrganization(keycloak.token, organizaton);
                toast.success(`${t("Workspace added")}`, {
                    position: toast.POSITION.TOP_RIGHT
                });
                onToggleModal(true);
            }
        }
    }

    async function handleUpdateOrganization() {
        if (!formIsValid()) return;
        else {
            if (keycloak.token && org_id) {
                await updateOrganization(keycloak.token, org_id, organizaton);
                toast.success(`${t("Workspace updated")}`, {
                    position: toast.POSITION.TOP_RIGHT
                });
                onToggleModal(true);
            }
        }
    }

    return (
        <OrganizationModal
            organization={organizaton}
            onToggleModal={toggleModalMouseEvent}
            onChange={handleChange}
            onSave={newOrg ? handleAddOrganization : handleUpdateOrganization}
            newOrg={newOrg}
            errors={errors}
            saveDisabled={saveDisabled}
        />
    )
}